import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import {
  useGetInvoiceByIdQuery,
  useRecreateInstallmentTokenMutation,
} from '../../store/api';
import { toast } from 'react-toastify';
import { buildInvoiceRelationParams } from './invoiceApiHelpers';
import InvoiceDetailContent, { invoiceStatusChip } from './InvoiceDetailContent';

export default function InvoiceRecreateInstallmentModal({
  open,
  invoiceId,
  onClose,
  onSuccess,
}) {
  const [amount, setAmount] = useState('');

  const {
    data,
    isFetching: loading,
    isError,
    error,
  } = useGetInvoiceByIdQuery(
    { id: invoiceId, params: buildInvoiceRelationParams() },
    { skip: !open || !invoiceId }
  );

  const [recreateInstallmentToken, { isLoading: submitting }] =
    useRecreateInstallmentTokenMutation();

  const inv = open && invoiceId ? data?.data ?? data ?? null : null;

  useEffect(() => {
    if (!open || !invoiceId) {
      setAmount('');
      return;
    }
    if (!inv) return;
    const defaultAmount = inv?.dues_amount ?? inv?.amount ?? '';
    setAmount(
      defaultAmount !== '' && defaultAmount != null ? String(defaultAmount) : ''
    );
  }, [open, invoiceId, inv]);

  useEffect(() => {
    if (open && isError) {
      toast.error(error?.data?.message || 'Failed to load invoice');
      onClose();
    }
  }, [open, isError, error, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!invoiceId) return;
    const trimmed = String(amount).trim();
    if (!trimmed || Number.isNaN(Number(trimmed)) || Number(trimmed) <= 0) {
      toast.warn('Enter a valid amount');
      return;
    }

    try {
      const fd = new FormData();
      fd.append('_method', 'PUT');
      fd.append('amount', trimmed);
      await recreateInstallmentToken({ id: invoiceId, data: fd }).unwrap();
      toast.success('Installment token recreated');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to recreate installment token');
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6" component="span" fontWeight={600}>
            Recreate installment
          </Typography>
          {inv ? invoiceStatusChip(inv) : null}
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 200 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress />
          </Box>
        ) : inv ? (
          <Box component="form" id="recreate-installment-form" onSubmit={handleSubmit}>
            <InvoiceDetailContent inv={inv} />
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              New installment amount
            </Typography>
            <TextField
              fullWidth
              required
              type="number"
              label="Amount"
              name="amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              disabled={submitting}
              size="small"
              sx={{ maxWidth: 320 }}
            />
          </Box>
        ) : (
          <Typography color="text.secondary">Invoice not available.</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="recreate-installment-form"
          variant="contained"
          disabled={loading || submitting || !inv}
        >
          {submitting ? 'Submitting…' : 'Recreate installment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
